import { BucketSelector } from '@repo/advanced-filters';
import { type BaseOperatorType, type BaseOptionsType } from 'advanced-filters/data';
import type { FilterLevel } from 'advanced-filters/types/filter';
import { useClientReadableCriteria } from 'crm-atoms/hooks';
import {
  type ReactNode, useCallback, useEffect, useMemo, useState,
} from 'react';

import AdvancedFilterCriteriaBuilder from '../AdvancedFilterCriteriaBuilder';

export interface BuilderUiProps {
  setBuilderOpen: (open: boolean) => void;
  handleDelete: () => void;
  selectedFiltersUi: ReactNode;
  appliedFilters?: {
    levels: FilterLevel[],
    comparator: BaseOperatorType,
  };
}

export default function CriteriaConditionsWrapper({
  criteriaId,
  onCriteriaChange,
  builderUi,
  additionalConditionKeys,
  textOnly = false,
  renderFilters = false,
  excludedDefaultKeys = [],
  savedTriggerConditionKeys = [],
}: {
  criteriaId?: number;
  onCriteriaChange: (savedCriteriaId?: number) => void;
  builderUi: (props: BuilderUiProps) => ReactNode;
  additionalConditionKeys?: BaseOptionsType[];
  textOnly?: boolean;
  renderFilters?: boolean;
  excludedDefaultKeys?: string[];
  savedTriggerConditionKeys?: any[];
}) {
  const [builderOpen, setBuilderOpen] = useState(false);

  const handleBuilderClose = useCallback(() => {
    setBuilderOpen(false);
  }, []);

  const handleBuilderSave = useCallback((newCriteriaId?: number) => {
    onCriteriaChange(newCriteriaId);
    handleBuilderClose();
  }, [handleBuilderClose, onCriteriaChange]);

  const {
    handleApplyFilter,
    appliedFilters,
  } = useClientReadableCriteria(criteriaId, false);

  useEffect(() => {
    handleApplyFilter();
  }, [criteriaId, handleApplyFilter]);

  const selectedFiltersUi = useMemo(
    () => {
      if (appliedFilters?.levels.length) {
        return (
          <BucketSelector
            buckets={appliedFilters.levels}
            baseComparator={appliedFilters.comparator}
            textOnly={textOnly}
            canEdit={false}
          />
        );
      }
      return null;
    },
    [appliedFilters, textOnly],
  );

  const builderUiWithProps = useMemo(
    () => builderUi({
      setBuilderOpen,
      handleDelete: () => handleBuilderSave(),
      selectedFiltersUi,
      appliedFilters,
    }),
    [builderUi, selectedFiltersUi, appliedFilters, handleBuilderSave],
  );

  return (
    <>
      {builderOpen && (
        <AdvancedFilterCriteriaBuilder
          open={builderOpen}
          onClose={handleBuilderClose}
          onSave={handleBuilderSave}
          existingCriteriaId={criteriaId}
          additionalConditionKeys={additionalConditionKeys}
          excludedDefaultKeys={excludedDefaultKeys}
          savedTriggerConditionKeys={savedTriggerConditionKeys}
        />
      )}
      {builderUiWithProps}
      {renderFilters && selectedFiltersUi}
    </>
  );
}
